import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../../services/ApiService";
import { StudentData } from "../../model/StudentData";

interface StudentsState {
  students: StudentData[];
  isLoading: boolean;
  error: string | null;
}

const initialState: StudentsState = {
  students: [],
  isLoading: false,
  error: null,
};

export const fetchStudents = createAsyncThunk<
  StudentData[],
  string,
  { rejectValue: string }
>("students/fetchStudents", async (classId, { rejectWithValue }) => {
  const res = await apiService.getStudentsByClass(classId);
  if (res.success && res.data) return res.data as StudentData[];
  return rejectWithValue(res.message || "Failed to fetch students");
});

export const createStudent = createAsyncThunk<
  StudentData,
  { classId: string; name: string },
  { rejectValue: string }
>("students/createStudent", async ({ classId, name }, { rejectWithValue }) => {
  const res = await apiService.createStudent(classId, { name });
  if (res.success && res.data) return res.data as StudentData;
  return rejectWithValue(res.message || "Failed to create student");
});

export const updateStudent = createAsyncThunk<
  StudentData,
  {
    id: string;
    name?: string;
    embedding1?: number[];
    embedding2?: number[];
    embedding3?: number[];
  },
  { rejectValue: string }
>("students/updateStudent", async ({ id, ...rest }, { rejectWithValue }) => {
  const res = await apiService.updateStudent(id, rest);
  if (res.success && res.data) return res.data as StudentData;
  return rejectWithValue(res.message || "Failed to update student");
});

export const deleteStudent = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("students/deleteStudent", async (id, { rejectWithValue }) => {
  const res = await apiService.deleteStudent(id);
  if (res.success || res.status === 204) return id;
  return rejectWithValue(res.message || "Failed to delete student");
});

const studentsSlice = createSlice({
  name: "students",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearStudents: (state) => {
      state.students = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.students = action.payload;
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to fetch students";
      })
      .addCase(createStudent.fulfilled, (state, action) => {
        state.students.push(action.payload);
      })
      .addCase(createStudent.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to create student";
      })
      .addCase(updateStudent.fulfilled, (state, action) => {
        const idx = state.students.findIndex(
          (s) => s.id === action.payload.id,
        );
        if (idx !== -1) state.students[idx] = action.payload;
      })
      .addCase(updateStudent.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to update student";
      })
      .addCase(deleteStudent.fulfilled, (state, action) => {
        state.students = state.students.filter((s) => s.id !== action.payload);
      })
      .addCase(deleteStudent.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to delete student";
      });
  },
});

export const { clearError, clearStudents } = studentsSlice.actions;

export const selectStudents = (state: { students: StudentsState }) =>
  state.students.students;
export const selectStudentsLoading = (state: { students: StudentsState }) =>
  state.students.isLoading;
export const selectStudentsError = (state: { students: StudentsState }) =>
  state.students.error;

export default studentsSlice.reducer;
